import { useState, useEffect, useCallback, useRef } from 'react';
import { usePermissions } from '../contexts/PermissionContext';
import api from '../services/api';

/**
 * Hook that resolves Instant Windscreens codes for a set of SKUs.
 * Only fetches when the logged-in user has the search_instant_sku permission.
 *
 * Usage:
 *   const { getInstantCode, canSeeInstant } = useInstantCode(['M2832', 'OZ_COMMBLK']);
 *   if (canSeeInstant) <span>Instant: {getInstantCode('M2832')}</span>
 */
const useInstantCode = (skus = []) => {
  const { hasPermission } = usePermissions();
  const canSeeInstant = hasPermission('search_instant_sku', 'can_access');
  const [codes, setCodes] = useState({});
  const lastKeyRef = useRef('');

  const fetchCodes = useCallback(async (skuList) => {
    if (!canSeeInstant || !skuList.length) return;
    const key = [...skuList].sort().join(',');
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;
    try {
      const resp = await api.get('/products/instant-codes', {
        params: { skus: key },
        validateStatus: s => s < 500
      });
      if (resp.status === 200 && resp.data?.codes) {
        setCodes(prev => ({ ...prev, ...resp.data.codes }));
      }
    } catch {
      // silently fail — instant codes are supplemental
    }
  }, [canSeeInstant]);

  useEffect(() => {
    const filtered = skus.filter(Boolean);
    if (filtered.length) fetchCodes(filtered);
  }, [skus.join(','), fetchCodes]); // eslint-disable-line react-hooks/exhaustive-deps

  const getInstantCode = useCallback((sku) => codes[sku] || null, [codes]);

  return { getInstantCode, canSeeInstant, codes };
};

export default useInstantCode;
